import React from "react";
import { Modal, Form, Input, Button, message } from "antd";
import { useUserPasswordUpdate } from "../../hooks/api/user/hooks";

import { useMessageApi } from "../../provider/AntdNotification";

interface PasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PasswordModal: React.FC<PasswordModalProps> = ({ isOpen, onClose }) => {
  const messageApi = useMessageApi();
  const [form] = Form.useForm();

  const updatePasswordMutation = useUserPasswordUpdate({
    onSuccess: (res) => {
      messageApi.success(res.message || "Password updated successfully!");
      form.resetFields();
      onClose();
    },
    onError: (err: any) => {
      messageApi.error(err?.message || "Failed to update password");
    },
  });

  const handleSubmit = (values: any) => {
    updatePasswordMutation.mutate({
      old_password: values.old_password,
      password: values.password,
      confirm_password: values.confirm_password,
    } as any);
  };

  return (
    <Modal
      title="Update Password"
      open={isOpen}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      footer={[
        <Button
          key="cancel"
          onClick={() => {
            form.resetFields();
            onClose();
          }}
          disabled={updatePasswordMutation.isPending}
        >
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={updatePasswordMutation.isPending}
          onClick={() => form.submit()}
        >
          Update Password
        </Button>,
      ]}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="mt-4"
      >
        <Form.Item
          name="old_password"
          label="Current Password"
          rules={[{ required: true, message: "Please enter your current password" }]}
        >
          <Input.Password placeholder="Enter current password" />
        </Form.Item>

        <Form.Item
          name="password"
          label="New Password"
          rules={[
            { required: true, message: "Please enter a new password" },
            { min: 6, message: "Password must be at least 6 characters" },
          ]}
        >
          <Input.Password placeholder="Enter new password" />
        </Form.Item>

        <Form.Item
          name="confirm_password"
          label="Confirm New Password"
          dependencies={["password"]}
          rules={[
            { required: true, message: "Please confirm your new password" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error("Passwords do not match"));
              },
            }),
          ]}
        >
          <Input.Password placeholder="Confirm new password" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PasswordModal;
